// @flow

import React from "react";
import { isValid } from "date-fns";
import { SectionComponent, GSGrid, Button, Checkbox, View } from "@components";
import { localize } from "redux-i18n";
import { isAfter } from "date-fns";
import type { PropertiesSchema } from "@types";
import type { ReasonToBeExcluded } from "@modules/service/reducer/reporting-parameters/types";
import { formatDateTime } from "@transformers/date";
import ReasonsToBeExcludedModal from "../reasons-to-be-excluded-modal";
import DeleteReasonsToBeExcludedConfirmModal from "../delete-reasons-to-be-excluded-confirm-modal";

type Props = $Exact<{
  isModalOpen: boolean,
  isConfirmDeleteModalOpen: boolean,
  clientId: ?number,
  schema: ?PropertiesSchema,
  data: Array<ReasonToBeExcluded>,
  editReasonToBeExcluded: ?ReasonToBeExcluded,
  toggleModal: Function,
  toggleConfirmDeleteModal: Function,
  createReasonToBeExcluded: Function,
  updateReasonToBeExcluded: Function,
  deleteReasonToBeExcluded: Function,
  setEditReasonToBeExcluded: Function,
  ...PropsFromLocalize,
}>;

type ComponentState = $Exact<{
  selectedLines: Array<number>,
  singleDeletionId: number | null,
}>;

export class ReasonsToBeExcluded extends React.Component<Props, ComponentState> {
  state = {
    selectedLines: [],
    singleDeletionId: null,
  };

  showModal = () => {
    this.props.toggleModal(true);
  };

  showEditModal = (item: ReasonToBeExcluded) => {
    this.props.setEditReasonToBeExcluded(item);
    this.showModal();
  };

  closeModal = () => {
    this.props.toggleModal(false);
    this.props.setEditReasonToBeExcluded(null);
  };

  showConfirmDeleteModal = (singleDeletionId?: number) => {
    if (singleDeletionId) {
      this.setState({
        singleDeletionId,
      });
    }
    this.props.toggleConfirmDeleteModal(true);
  };

  closeConfirmDeleteModal = () => {
    this.props.toggleConfirmDeleteModal(false);
    this.setState({
      singleDeletionId: null,
    });
  };

  handleSubmit = (formData: Object) => {
    if (this.props.editReasonToBeExcluded) {
      this.props.updateReasonToBeExcluded({
        clientId: this.props.clientId,
        formData,
        reasonToBeExcludedId: this.props.editReasonToBeExcluded.id,
      });
    } else {
      this.props.createReasonToBeExcluded({ clientId: this.props.clientId, formData });
    }
    this.closeModal();
  };

  handleDelete = () => {
    if (this.state.singleDeletionId) {
      this.props.deleteReasonToBeExcluded({
        clientId: this.props.clientId,
        reasonToBeExcludedIdList: [this.state.singleDeletionId],
      });
    } else {
      this.props.deleteReasonToBeExcluded({
        clientId: this.props.clientId,
        reasonToBeExcludedIdList: this.state.selectedLines,
      });
      this.deselectAll();
    }
    this.closeConfirmDeleteModal();
  };

  validate = (formData: Object, errors: Object) => {
    if (formData.excludeFrom && !isValid(new Date(formData.excludeFrom))) {
      errors.excludeFrom.addError(this.props.t("INVALID_DATE"));
    }

    if (formData.excludeTo && !isValid(new Date(formData.excludeTo))) {
      errors.excludeTo.addError(this.props.t("INVALID_DATE"));
    }

    if (isAfter(new Date(formData.excludeFrom), new Date(formData.excludeTo))) {
      errors.excludeTo.addError(this.props.t("EXCLUDE_FROM_IS_AFTER_TO"));
    }

    return errors;
  };

  selectAll = () => {
    if (this.state.selectedLines.length === this.props.data.length) {
      this.deselectAll();
    } else {
      this.setState({
        selectedLines: this.props.data.map(item => item.id),
      });
    }
  };

  deselectAll = () => {
    this.setState({
      selectedLines: [],
    });
  };

  onCheckboxClick = (id: number) => {
    if (this.state.selectedLines.includes(id)) {
      this.setState({
        selectedLines: this.state.selectedLines.filter(line => line !== id),
      });
    } else {
      this.setState({
        selectedLines: [...this.state.selectedLines, id],
      });
    }
  };

  onDeleteSelectedLines = () => {
    this.showConfirmDeleteModal();
  };

  get tableConfig() {
    return [
      {
        key: "absenceReason",
        name: this.props.t("ABSENCE_REASON_NOT_INCLUDED"),
        align: "left",
        renderCell: ({ row }: { row: Object }) => row.absenceReason.label,
      },
      {
        key: "excludeFrom",
        name: this.props.t("DATE_EXCLUDE_FROM"),
        renderCell: ({ row }: { row: Object }) => formatDateTime(row.excludeFrom),
      },
      {
        key: "excludeTo",
        name: this.props.t("DATE_EXCLUDE_TO"),
        renderCell: ({ row }: { row: Object }) => formatDateTime(row.excludeTo),
      },
      {
        key: "edit",
        name: this.props.t("EDIT"),
        align: "center",
        width: 70,
        renderCell: ({ row }: { row: ReasonToBeExcluded }) => (
          <span
            onClick={() => this.showEditModal(row)}
            className="material-icons pointer font-size-26"
          >
            edit
          </span>
        ),
      },
      {
        key: "delete",
        name: this.props.t("DELETE"),
        align: "center",
        width: 70,
        renderCell: ({ row }: { row: ReasonToBeExcluded }) => (
          <span
            onClick={() => this.showConfirmDeleteModal(row.id)}
            className="material-icons pointer font-size-26"
          >
            delete
          </span>
        ),
      },
    ];
  }

  get reasonsToBeExcludedLabels(): Array<string> {
    if (this.state.singleDeletionId) {
      const selectedReason = this.props.data.find(item => item.id === this.state.singleDeletionId);
      return selectedReason ? [selectedReason.absenceReason.label] : [""];
    }
    return this.props.data
      .filter(item => this.state.selectedLines.includes(item.id))
      .map(item => item.absenceReason.label);
  }

  handleOnSelectedRowsChange = (selectedLines: number[]) => {
    this.setState({
      selectedLines,
    });
  };

  render() {
    return (
      <View className="gs-container" flex={1} gap={6}>
        <SectionComponent
          title={<h6>{this.props.t("BRADFORD_SCORES_EXCLUSIONS")}</h6>}
          rightLink={
            <Button materialIcon="add_circle" onClick={this.showModal}>
              {this.props.t("ADD")}
            </Button>
          }
          wrapperClassName="flex-column reasons-to-be-excluded-table"
        >
          <GSGrid
            selectedRows={this.state.selectedLines}
            onSelectedRowsChange={this.handleOnSelectedRowsChange}
            columns={this.tableConfig}
            rows={this.props.data}
          />
          {this.state.selectedLines.length ? (
            <div className="d-flex flex-row actions align-items-center pl-5">
              <div className="text-warning mr-3">
                {this.props.t("NUMBER_RECORDS_SELECTED", {
                  number: this.state.selectedLines.length,
                })}
              </div>
              <Button size="small" color="negative" onClick={this.onDeleteSelectedLines}>
                {this.props.t("DELETE")}
              </Button>
              <Button color="secondary" onClick={this.deselectAll}>
                {this.props.t("DESELECT_ALL")}
              </Button>
            </div>
          ) : null}
          {this.props.schema && this.props.isModalOpen && (
            <ReasonsToBeExcludedModal
              schema={this.props.schema}
              reasonToBeExcluded={this.props.editReasonToBeExcluded}
              onDismissHandler={this.closeModal}
              onSubmit={this.handleSubmit}
              validate={this.validate}
            />
          )}
          {this.props.isConfirmDeleteModalOpen && (
            <DeleteReasonsToBeExcludedConfirmModal
              labels={this.reasonsToBeExcludedLabels}
              onDismissHandler={this.closeConfirmDeleteModal}
              onSubmit={this.handleDelete}
            />
          )}
        </SectionComponent>
      </View>
    );
  }
}

export default localize()(ReasonsToBeExcluded);
